import React, { useEffect, useState } from 'react';
import './sync-maps-vehicle-card.scss';
import { useBreakpoints } from '../../../hooks/use-breakpoints';
import { useSyncMapsVehicleContent } from './hooks/use-sync-maps-vehicle-card-content';
import AppConfigurationService from '../../../services/app-configuration-service/app-configuration-service';
import AccordionItem from '../../../support/components/AccordionItem/AccordionItem';
import { SyncMapsConsolidatedResponse } from '../../../views/sync-maps-updates-view/hooks/use-syncs-maps-status-updates';
import { useSupportCommonContent } from '../../../support/components/astute-topic/hook/use-support-common';
import { SyncMapsVehicleCardGeneration } from './sync-maps-vehicle-card-generation';
import { nameplateMakeModelYearUtil } from '../../utils/nameplate-model-year-util/nameplate-make-model-year-util';
import { SyncCitivanCheckVersionProps } from '../../../views/sync-maps-updates-view/hooks/use-sync-citivan-check-a-version';

interface Props {
    syncMapsConsolidatedResponse: SyncMapsConsolidatedResponse;
    hasCtr?: boolean;
    ctrHide?: boolean;
    hasSync?: boolean;
    hasVW?: boolean;
    releaseDate?: string;
    vwContent?: SyncCitivanCheckVersionProps | null;
}

export const cleanVersion = (version: string): string => {
    const splitVersion = version.split(' ');
    const cleanedVersion = splitVersion[splitVersion.length - 1]
        .toLowerCase()
        .replace('v', '');
    return cleanedVersion || '';
};

const SyncMapsVehicleCard = (props: Props) => {
    const cardName =
        props.hasCtr && !props.ctrHide
            ? 'ctr-vehicle-card'
            : 'sync-maps-vehicle-card';
    const vehicleCardContent = useSyncMapsVehicleContent(cardName);
    const supportCommonContent = useSupportCommonContent();

    const vehicleAttributes =
        props.syncMapsConsolidatedResponse.vehicleAttributes;
    const syncData =
        props.syncMapsConsolidatedResponse?.syncMapUpdateResponse
            ?.syncUpdateResponse;
    const mapData =
        props.syncMapsConsolidatedResponse.syncMapUpdateResponse
            ?.mapUpdateResponse;
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const { fmaRegion } = new AppConfigurationService().getAppConfiguration();
    const isEU = fmaRegion.toLowerCase() === 'eu';
    const { desktopOrLarger } = useBreakpoints();
    const isSync5 = syncData?.generation?.toString() === '5';

    const vwLabel = props.vwContent?.vwVersionLabel
        ? props.vwContent?.vwVersionLabel
        : supportCommonContent?.releaseDateLabel;

    const cardMaterial = (
        <section className="sync-maps-vehicle-card">
            {desktopOrLarger && (
                <h2 className="sync-maps-vehicle-name">
                    {nameplateMakeModelYearUtil(
                        vehicleAttributes.model,
                        vehicleAttributes.year,
                        vehicleAttributes.make
                    )}
                </h2>
            )}
            <hr className="sync-maps-divider" />
            <div className="sync-details">
                <div className="sync-details-labels">
                    {syncData?.generation && (
                        <h3 className="sync-maps-labels-subheader">
                            {isSync5
                                ? vehicleCardContent?.currentVersionLabel
                                : `${vehicleCardContent?.syncLabel} ${vehicleCardContent?.currentVersionLabel}`}
                        </h3>
                    )}
                    <p className="sync-maps-labels-row" data-cs-mask>
                        <span className="dark-text">
                            {vehicleCardContent?.vinLabel}
                        </span>
                        <span className="dark-gray-text">
                            {vehicleAttributes.vin}
                        </span>
                    </p>

                    <SyncMapsVehicleCardGeneration
                        syncData={syncData}
                        vehicleCardContent={vehicleCardContent}
                        hasCtr={props.hasCtr}
                        ctrHide={props.ctrHide}
                        hasSync={props.hasSync}
                        cleanVersion={cleanVersion}
                        isSync5={isSync5}
                    />
                </div>
            </div>
            {syncData?.hasFirmwareUpdate &&
                ((props.hasCtr && !props.ctrHide) || props.hasSync) && (
                    <>
                        <hr className="sync-maps-divider" />
                        <div className="sync-updates-details">
                            <h3 className="sync-maps-labels-subheader">
                                {`${vehicleCardContent?.syncLabel} ${vehicleCardContent?.updatesLabel}`}
                            </h3>
                            {syncData?.newSyncVersion && (
                                <p className="sync-maps-labels-row">
                                    <span className="dark-text">
                                        {
                                            vehicleCardContent?.updateAvailableLabel
                                        }
                                    </span>
                                    <span className="dark-gray-text">
                                        {cleanVersion(
                                            syncData.newSyncVersion
                                        ) || ''}
                                    </span>
                                </p>
                            )}
                        </div>
                    </>
                )}

            <hr className="sync-maps-divider" />
            {((isEU && mapData?.hasUpdate) ||
                (props.hasVW && props.releaseDate)) && (
                <div className={'maps-updates-details'}>
                    <h3 className="sync-maps-labels-subheader">
                        {vehicleCardContent?.mapsLabel}
                    </h3>
                    <p className="sync-maps-labels-row">
                        <span className="dark-text">
                            {props.hasVW
                                ? vwLabel
                                : vehicleCardContent?.mapsLabel}
                        </span>
                        <span className="dark-gray-text">
                            <div>
                                {props.hasVW
                                    ? props.syncMapsConsolidatedResponse
                                          ?.vwMapsUpdateResponse?.version
                                    : vehicleCardContent?.updateAvailableLabel}
                            </div>
                        </span>
                    </p>
                </div>
            )}
        </section>
    );
    useEffect(() => {
        setIsExpanded(syncData?.hasFirmwareUpdate || false);
    }, [syncData]);
    return (
        <>
            <div className="sync-maps-container">
                {desktopOrLarger ? (
                    cardMaterial
                ) : (
                    <AccordionItem
                        title={nameplateMakeModelYearUtil(
                            vehicleAttributes.model,
                            vehicleAttributes.year,
                            vehicleAttributes.make
                        )}
                        subtitle={
                            vehicleCardContent?.mobileAccordionExpandSubtitle
                        }
                        expanded={isExpanded}
                        id={'vehicle-card'}
                        tabIndex={0}
                    >
                        {cardMaterial}
                    </AccordionItem>
                )}
            </div>
        </>
    );
};

export default SyncMapsVehicleCard;
